import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { TrainingType } from '@strength/domain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../trpc/react';
import { MultiSelect } from '../../components/input/multi-select';
import { FocusOption } from '../library/schemes/types';
import { WithWorkoutId } from './context-menu/data-for-context-menu';
import { SetGroupedData } from './types';

export type CreateSchemePayload = {
  setsData: WithWorkoutId<SetGroupedData>[];
  name: string;
  type: TrainingType;
  focusIds: string[];
};

export const NewSchemeDialog = ({
  setsData,
  focusOptions,
  onClose,
  onCreateScheme,
}: {
  setsData: WithWorkoutId<SetGroupedData>[];
  focusOptions: FocusOption[];
  onClose: () => void;
  onCreateScheme: (sets: CreateSchemePayload) => void;
}) => {
  const [name, setName] = useState('');
  const [focusIds, setFocusIds] = useState<string[]>([]);
  const { t } = useTranslation();

  if (setsData.length === 0) {
    throw new Error('No sets data provided for scheme');
  }

  const exerciseForScheme = setsData[0]?.data.exercise;
  if (exerciseForScheme === undefined) {
    throw new Error('No exercise data provided for scheme');
  }

  const libraryExerciseQuery = trpc.exerciseLibrary.exerciseById.useQuery({
    libraryExerciseId: exerciseForScheme.libraryExerciseId,
  });

  const type = libraryExerciseQuery.data?.trainingType || undefined;

  return (
    <Dialog open={setsData !== undefined} onClose={onClose} fullWidth>
      <DialogTitle>{t('saveScheme')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl>
          <FormLabel>{t('name')}</FormLabel>
          <TextField onChange={(e) => setName(e.target.value)} value={name}>
            {name}
          </TextField>
        </FormControl>
        <FormControl disabled>
          <FormLabel>{t('type')}</FormLabel>
          <Select fullWidth value={type?.id ?? null}>
            {type && (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>{t('focuses')}</FormLabel>
          <MultiSelect
            selectedOptions={focusOptions.filter((f) =>
              focusIds.includes(f.id),
            )}
            options={focusOptions.filter((f) => f.trainingTypeId === type?.id)}
            onChange={(e) => {
              setFocusIds(e.map((f) => f.id));
            }}
            optionToName={(f) => f.name}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={name === '' || type === undefined}
          onClick={() => {
            type &&
              onCreateScheme({
                name,
                type,
                focusIds,
                setsData,
              });
            onClose();
          }}
          type="submit"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
